// Dedicated per-member rigs for Verdant squads whose silhouettes cannot be expressed through
// the shared humanoid instancing path. Each factory returns its own pose closure so the live
// renderer and developer model viewer animate exactly the same production geometry.

import * as THREE from 'three';
import { OWNER_COLORS } from './unitStyles';

export const BESPOKE_VERDANT_SQUAD_DEFS = new Set([
  'u-vw-sproutling-swarm',
  'u-vw-sporecallers',
  'u-vw-sunpetal-seraphs',
  'u-vw-thorn-guard',
  'u-vw-vine-archers',
  'u-vw-root-mender',
  'u-vw-canopy-stalkers',
  'u-vw-groveback',
  'u-vw-briarback-wardens',
  'u-vw-ancient-treants',
  'u-vw-thornmaw-hydra',
  'u-vw-verdant-behemoth',
]);

export type VerdantAbilityCue = 'harmful' | 'healing' | 'solar' | 'seed' | null;

export interface VerdantPoseInput {
  time: number;
  phase: number;
  moveAmt: number;
  strike: number;
  ability: VerdantAbilityCue;
  /** 0 upright, 1 fully ragdolled */
  knockdown?: number;
}

export interface VerdantSquadRig {
  group: THREE.Group;
  pose: (input: VerdantPoseInput) => void;
}

function mix(a: number, b: number, t: number): THREE.Color {
  return new THREE.Color(a).lerp(new THREE.Color(b), t);
}

function mat(color: number | THREE.Color, rough = 0.9, metal = 0.02): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: color instanceof THREE.Color ? color : new THREE.Color(color),
    roughness: rough,
    metalness: metal,
    flatShading: true,
  });
}

function glow(color: number, intensity = 1): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    emissive: color,
    emissiveIntensity: intensity,
    roughness: 0.42,
    flatShading: true,
  });
}

function finish(group: THREE.Group, scale: number, shadowRadius: number): void {
  const shadow = new THREE.Mesh(
    new THREE.CircleGeometry(shadowRadius, 18),
    new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.2, depthWrite: false }),
  );
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = 0.012;
  group.add(shadow);
  group.scale.setScalar(scale);
  group.traverse((o) => {
    if (o instanceof THREE.Mesh && o.material instanceof THREE.MeshStandardMaterial) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });
}

function limb(name: string, material: THREE.Material, x: number, y: number, length: number, radius: number): THREE.Group {
  const pivot = new THREE.Group();
  pivot.name = name;
  pivot.position.set(x, y, 0);
  const segment = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.72, radius, length, 6), material);
  segment.position.y = -length / 2;
  pivot.add(segment);
  return pivot;
}

function petal(material: THREE.Material, length: number, width: number): THREE.Mesh {
  const p = new THREE.Mesh(new THREE.SphereGeometry(0.5, 7, 5), material);
  p.scale.set(width, length, width * 0.42);
  return p;
}

function addFlower(parent: THREE.Group, variant: number): THREE.Group {
  const bloom = new THREE.Group();
  bloom.name = 'headGrowth';
  bloom.position.y = 0.84;
  const palettes = [
    [0xf3eee0, 0xe8b83f],
    [0xf06a79, 0xffd36a],
    [0x79a9ef, 0xd9e8ff],
    [0xd987d6, 0xffd778],
    [0xffcc4f, 0x70411f],
  ] as const;
  if (variant === 4 || variant === 5 || variant === 7) {
    const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.055, 0.28, 6), mat(0xe2d5ac));
    stem.position.y = 0.1;
    bloom.add(stem);
    const capColor = variant === 4 ? 0xe75549 : variant === 5 ? 0x9b65c4 : 0x4c9cb0;
    const cap = new THREE.Mesh(
      new THREE.SphereGeometry(0.22, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2),
      mat(capColor, 0.88),
    );
    cap.position.y = 0.24;
    cap.scale.y = 0.65;
    bloom.add(cap);
    for (let i = 0; i < 5; i++) {
      const spot = new THREE.Mesh(new THREE.SphereGeometry(0.025, 5, 4), mat(0xffeac4));
      const a = (i / 5) * Math.PI * 2;
      spot.position.set(Math.cos(a) * 0.12, 0.36, Math.sin(a) * 0.12);
      bloom.add(spot);
    }
  } else {
    const palette = palettes[variant % palettes.length];
    const petals = variant === 6 ? 10 : 6 + (variant % 2) * 2;
    const petalMat = mat(palette[0], 0.82);
    for (let i = 0; i < petals; i++) {
      const a = (i / petals) * Math.PI * 2;
      const p = petal(petalMat, variant === 6 ? 0.24 : 0.2, variant === 6 ? 0.075 : 0.085);
      p.position.set(Math.cos(a) * 0.15, Math.sin(a) * 0.15 + 0.15, 0);
      p.rotation.z = a - Math.PI / 2;
      bloom.add(p);
    }
    const center = new THREE.Mesh(new THREE.SphereGeometry(variant === 6 ? 0.12 : 0.09, 8, 6), mat(palette[1], 0.72));
    center.position.y = 0.15;
    center.position.z = 0.03;
    bloom.add(center);
  }
  parent.add(bloom);
  return bloom;
}

function makeSproutling(owner: 0 | 1, variant: number, uiScale: number): VerdantSquadRig {
  const group = new THREE.Group();
  const bark = mat(mix(0x9b7144, OWNER_COLORS[owner], 0.08), 0.98);
  const barkDark = mat(0x4e3420, 1);
  const leaf = mat(mix(0x77ad4b, OWNER_COLORS[owner], 0.1), 0.96);
  const face = mat(0x2d2016, 1);

  const body = new THREE.Mesh(new THREE.IcosahedronGeometry(0.36, 1), bark);
  body.position.y = 0.54;
  body.scale.set(1.05, 0.95, 0.96);
  group.add(body);
  const legs: THREE.Group[] = [];
  for (const sx of [-1, 1]) {
    const leg = new THREE.Group();
    leg.position.set(sx * 0.17, 0.31, 0);
    const root = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.09, 0.3, 5), barkDark);
    root.position.y = -0.15;
    root.rotation.z = sx * 0.1;
    leg.add(root);
    const toe = new THREE.Mesh(new THREE.ConeGeometry(0.08, 0.25, 5), barkDark);
    toe.rotation.x = Math.PI / 2;
    toe.position.set(0, -0.29, 0.1);
    leg.add(toe);
    group.add(leg);
    legs.push(leg);
  }
  const arms: THREE.Group[] = [];
  for (const sx of [-1, 1]) {
    const arm = new THREE.Group();
    arm.position.set(sx * 0.32, 0.58, 0.02);
    const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.05, 0.3, 5), barkDark);
    stem.position.y = -0.12;
    stem.rotation.z = sx * -0.35;
    arm.add(stem);
    const handLeaf = petal(leaf, 0.18, 0.09);
    handLeaf.position.set(sx * 0.09, -0.26, 0.02);
    handLeaf.rotation.z = sx * -0.7;
    arm.add(handLeaf);
    group.add(arm);
    arms.push(arm);
  }

  // Carved eyes, brows, and a variant smile give every member a readable expression.
  for (const sx of [-1, 1]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.045, 6, 4), face);
    eye.position.set(sx * 0.12, 0.61 + (variant % 2) * 0.015, 0.3);
    group.add(eye);
    const brow = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.025, 0.025), face);
    brow.position.set(sx * 0.12, 0.69, 0.315);
    brow.rotation.z = sx * (variant % 3 - 1) * 0.12;
    group.add(brow);
  }
  const mouth = new THREE.Mesh(new THREE.TorusGeometry(0.09, 0.017, 5, 10, Math.PI), face);
  mouth.position.set(0, 0.5, 0.315);
  mouth.rotation.z = variant % 3 === 0 ? 0 : Math.PI;
  group.add(mouth);
  const bloom = addFlower(group, variant);
  finish(group, uiScale, 0.5);

  return {
    group,
    pose: ({ time, phase, moveAmt, strike, ability, knockdown = 0 }) => {
      const pollen = ability === 'healing' ? 1 : 0;
      const stride = Math.sin(phase) * 0.65 * moveAmt;
      legs[0].rotation.x = stride;
      legs[1].rotation.x = -stride;
      const wave = Math.sin(time * 1.7 + variant * 0.8) * (0.09 + 0.04 * (1 - moveAmt));
      arms[0].rotation.z = wave - strike * 0.55 - pollen * 0.6;
      arms[1].rotation.z = -wave + strike * 0.3 + pollen * 0.6;
      body.scale.y = 0.95 + Math.sin(time * 2.2 + variant) * 0.035 * (1 - moveAmt) - strike * 0.08 + pollen * 0.08;
      body.rotation.x = strike * 0.25;
      group.rotation.z = knockdown * (variant % 2 === 0 ? 1 : -1) * Math.PI * 0.48;
      bloom.rotation.z = Math.sin(time * 1.25 + variant) * 0.06 + Math.sin(phase) * 0.04 * moveAmt;
      bloom.rotation.x = Math.cos(time * 1.1 + variant) * 0.035;
      bloom.scale.setScalar(1 + pollen * (0.18 + Math.sin(time * 7 + variant) * 0.05));
    },
  };
}

function smallMushroom(parent: THREE.Object3D, x: number, y: number, z: number, color: number, scale = 1): void {
  const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.025 * scale, 0.035 * scale, 0.16 * scale, 5), mat(0xd8cfb0));
  stem.position.set(x, y, z);
  parent.add(stem);
  const cap = new THREE.Mesh(
    new THREE.SphereGeometry(0.09 * scale, 8, 5, 0, Math.PI * 2, 0, Math.PI / 2),
    mat(color, 0.9),
  );
  cap.position.set(x, y + 0.09 * scale, z);
  cap.scale.y = 0.65;
  parent.add(cap);
}

function makeSporecaller(owner: 0 | 1, variant: number, uiScale: number): VerdantSquadRig {
  const group = new THREE.Group();
  const moss = mat(mix(0x536f3f, OWNER_COLORS[owner], 0.12), 0.98);
  const mossLight = mat(0x7f9b59, 0.98);
  const stemMat = mat(0xc5b994, 0.94);
  const capColors = [0x8d5e9c, 0xa56d55, 0x557f72];
  const capMat = mat(mix(capColors[variant % capColors.length], OWNER_COLORS[owner], 0.08), 0.9);
  const wood = mat(0x57412b, 0.98);
  const harmMat = glow(0x5a3d6e, 0.65);
  const healMat = glow(0xa9e878, 0.9);

  const robe = new THREE.Mesh(new THREE.ConeGeometry(0.38, 1.05, 8), moss);
  robe.position.y = 0.58;
  group.add(robe);
  const robeLayer = new THREE.Mesh(new THREE.ConeGeometry(0.43, 0.42, 9), mossLight);
  robeLayer.position.y = 0.34;
  group.add(robeLayer);
  const shoulders = new THREE.Mesh(new THREE.IcosahedronGeometry(0.34, 0), mossLight);
  shoulders.position.set(0, 0.98, -0.02);
  shoulders.scale.set(1.15, 0.58, 0.85);
  group.add(shoulders);
  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.18, 0.38, 7), stemMat);
  neck.position.set(0, 1.18, 0.04);
  neck.rotation.x = 0.22;
  group.add(neck);
  const face = new THREE.Mesh(new THREE.SphereGeometry(0.18, 9, 6), stemMat);
  face.position.set(0, 1.34, 0.12);
  face.scale.set(0.88, 0.9, 0.75);
  group.add(face);
  for (const sx of [-1, 1]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.027, 6, 4), glow(0xd5f0a0, 0.8));
    eye.position.set(sx * 0.07, 1.36, 0.25);
    group.add(eye);
  }

  const capPivot = new THREE.Group();
  capPivot.position.set(0, 1.44, 0.02);
  const cap = new THREE.Mesh(new THREE.SphereGeometry(0.58, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2), capMat);
  cap.scale.set(1.05, 0.56, 0.95);
  capPivot.add(cap);
  const gills = new THREE.Mesh(new THREE.CylinderGeometry(0.46, 0.18, 0.08, 12), stemMat);
  gills.position.y = -0.025;
  capPivot.add(gills);
  group.add(capPivot);

  const staff = new THREE.Group();
  staff.position.set(0.43, 1.0, 0.02);
  const lower = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.055, 1.55, 6), wood);
  lower.position.y = -0.35;
  lower.rotation.z = -0.1;
  staff.add(lower);
  const crook = new THREE.Mesh(new THREE.TorusGeometry(0.2, 0.045, 6, 12, Math.PI * 1.2), wood);
  crook.position.set(-0.12, 0.42, 0);
  crook.rotation.z = 0.2;
  staff.add(crook);
  smallMushroom(staff, -0.09, 0.17, 0.04, 0xe26c89, 0.7);
  smallMushroom(staff, 0.05, -0.17, -0.03, 0x8fcf68, 0.55);
  group.add(staff);
  smallMushroom(group, -0.31, 1.02, 0.02, 0xe9a04f, 0.75);
  smallMushroom(group, 0.27, 1.06, -0.08, 0x8b70c9, 0.6);

  const darkSpores: THREE.Mesh[] = [];
  const healingSpores: THREE.Mesh[] = [];
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2 + variant;
    const dark = new THREE.Mesh(new THREE.DodecahedronGeometry(0.045 + (i % 3) * 0.012, 0), harmMat);
    dark.position.set(Math.cos(a) * (0.35 + (i % 2) * 0.18), 1.55 + (i % 3) * 0.15, Math.sin(a) * 0.32);
    dark.userData.baseY = dark.position.y;
    dark.visible = false;
    group.add(dark);
    darkSpores.push(dark);
    const bright = new THREE.Mesh(new THREE.SphereGeometry(0.035 + (i % 2) * 0.012, 6, 4), healMat);
    bright.position.copy(dark.position);
    bright.userData.baseY = bright.position.y;
    bright.visible = false;
    group.add(bright);
    healingSpores.push(bright);
  }
  const legs = [new THREE.Group(), new THREE.Group()];
  for (let i = 0; i < 2; i++) {
    const sx = i === 0 ? -1 : 1;
    legs[i].position.set(sx * 0.15, 0.28, 0);
    const foot = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.09, 0.32, 5), stemMat);
    foot.position.y = -0.16;
    legs[i].add(foot);
    group.add(legs[i]);
  }
  finish(group, uiScale, 0.68);

  return {
    group,
    pose: ({ time, phase, moveAmt, strike, ability }) => {
      const stride = Math.sin(phase) * 0.5 * moveAmt;
      legs[0].rotation.x = stride;
      legs[1].rotation.x = -stride;
      const harmful = ability === 'harmful';
      const healing = ability === 'healing';
      const open = Math.max(strike * 0.35, harmful || healing ? 1 : 0);
      capPivot.scale.set(1 + open * 0.16, 1 + open * 0.1, 1 + open * 0.16);
      capPivot.rotation.z = Math.sin(time * 0.85 + variant) * 0.025;
      staff.rotation.x = strike * -0.55 + Math.sin(time * 1.05 + variant) * 0.035 * (1 - moveAmt);
      robe.scale.y = 1 + Math.sin(time * 1.35 + variant) * 0.025 * (1 - moveAmt);
      darkSpores.forEach((s, i) => {
        s.visible = harmful;
        s.position.y = (s.userData.baseY as number) + (harmful ? Math.sin(time * 2.2 + i) * 0.06 : 0);
        s.scale.setScalar(0.8 + Math.sin(time * 3 + i) * 0.2);
      });
      healingSpores.forEach((s, i) => {
        s.visible = healing;
        s.position.y = (s.userData.baseY as number) + (healing ? Math.sin(time * 1.7 + i) * 0.07 : 0);
        s.scale.setScalar(0.85 + Math.sin(time * 2.4 + i) * 0.18);
      });
    },
  };
}

function makeSeraph(owner: 0 | 1, variant: number, uiScale: number): VerdantSquadRig {
  const group = new THREE.Group();
  const ivory = mat(mix(0xe8dfc3, OWNER_COLORS[owner], 0.05), 0.72, 0.04);
  const ivoryDark = mat(0xb7a985, 0.82);
  const gold = glow(0xf3c94f, 1.05);
  const sun = glow(0xffdf72, 1.7);
  const faceMark = mat(0x7a6630, 0.72);

  const torso = new THREE.Mesh(new THREE.CapsuleGeometry(0.22, 0.88, 5, 9), ivory);
  torso.position.y = 1.18;
  group.add(torso);
  const waist = new THREE.Mesh(new THREE.ConeGeometry(0.3, 0.62, 8), ivoryDark);
  waist.position.y = 0.65;
  group.add(waist);
  const head = new THREE.Group();
  head.position.set(0, 1.87, 0.04);
  const mask = new THREE.Mesh(new THREE.SphereGeometry(0.22, 10, 8), ivory);
  mask.scale.set(0.82, 1.08, 0.58);
  head.add(mask);
  for (const sx of [-1, 1]) {
    const eye = new THREE.Mesh(new THREE.BoxGeometry(0.055, 0.018, 0.02), faceMark);
    eye.position.set(sx * 0.07, 0.025, 0.13);
    eye.rotation.z = sx * -0.08;
    head.add(eye);
  }
  group.add(head);
  const arms: THREE.Group[] = [];
  for (const sx of [-1, 1]) {
    const arm = new THREE.Group();
    arm.position.set(sx * 0.29, 1.48, 0);
    const limb = new THREE.Mesh(new THREE.CapsuleGeometry(0.055, 0.56, 3, 6), ivory);
    limb.position.y = -0.31;
    arm.add(limb);
    const hand = new THREE.Mesh(new THREE.SphereGeometry(0.075, 7, 5), gold);
    hand.position.y = -0.66;
    arm.add(hand);
    group.add(arm);
    arms.push(arm);
  }

  const ringRoot = new THREE.Group();
  ringRoot.position.set(0, 1.27, -0.2);
  group.add(ringRoot);
  const rings: THREE.Group[] = [];
  const ringPetals: THREE.Mesh[][] = [];
  for (let r = 0; r < 3; r++) {
    const ring = new THREE.Group();
    ring.rotation.set(r === 1 ? 0.16 : -0.1, r === 2 ? 0.12 : 0, variant * 0.25 + r * 0.45);
    const petals: THREE.Mesh[] = [];
    const count = 8 + r * 2;
    const radius = 0.52 + r * 0.34;
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2;
      const p = petal(gold, 0.3 + r * 0.06, 0.09 + r * 0.015);
      p.position.set(Math.cos(a) * radius, Math.sin(a) * radius, -r * 0.035);
      p.rotation.z = a - Math.PI / 2;
      ring.add(p);
      petals.push(p);
    }
    ringRoot.add(ring);
    rings.push(ring);
    ringPetals.push(petals);
  }
  const core = new THREE.Mesh(new THREE.SphereGeometry(0.18, 10, 8), sun);
  core.position.z = -0.12;
  ringRoot.add(core);
  const halo = new THREE.Mesh(new THREE.TorusGeometry(0.29, 0.035, 6, 18), gold);
  halo.position.set(0, 2.18, 0);
  group.add(halo);
  finish(group, uiScale, 1.2);

  return {
    group,
    pose: ({ time, phase, moveAmt, strike, ability }) => {
      const solar = ability === 'solar' ? 1 : ability === 'seed' ? 0.55 : 0;
      rings.forEach((ring, i) => {
        ring.rotation.z = (i % 2 === 0 ? 1 : -1) * time * (0.22 + i * 0.08) + variant * 0.3;
        ring.rotation.y = Math.sin(time * 0.45 + i) * 0.08;
        ringPetals[i].forEach((p, j) => {
          const flare = solar * (0.38 + 0.08 * Math.sin(time * 5 + j));
          p.scale.y = (0.3 + i * 0.06) * (1 + flare);
          p.scale.x = (0.09 + i * 0.015) * (1 + flare * 0.45);
        });
      });
      const stride = Math.sin(phase) * 0.28 * moveAmt;
      arms[0].rotation.x = stride - strike * 0.5 - solar * 0.85;
      arms[1].rotation.x = -stride - strike * 0.5 - solar * 0.85;
      arms[0].rotation.z = solar * -0.85;
      arms[1].rotation.z = solar * 0.85;
      head.rotation.y = Math.sin(time * 0.45 + variant) * 0.08 * (1 - moveAmt);
      torso.scale.y = 1 + Math.sin(time * 1.5 + variant) * 0.025;
      halo.rotation.z = -time * 0.32;
      const sunMat = core.material as THREE.MeshStandardMaterial;
      sunMat.emissiveIntensity = 1.7 + solar * 2.8 + Math.sin(time * 2) * 0.18;
      ringRoot.scale.setScalar(1 + solar * 0.22);
    },
  };
}

function makeVerdantGuardian(defId: string, owner: 0 | 1, variant: number, uiScale: number): VerdantSquadRig {
  const group = new THREE.Group();
  const heavy = defId === 'u-vw-briarback-wardens';
  const stalker = defId === 'u-vw-canopy-stalkers';
  const bark = mat(mix(variant % 2 ? 0x68482f : 0x765137, OWNER_COLORS[owner], 0.09), 0.98);
  const barkDark = mat(0x3d2d20, 1);
  const leaf = mat(mix(0x5e9147, OWNER_COLORS[owner], 0.12), 0.98);
  const thorn = mat(0x9a7446, 0.9);
  const sap = glow(defId === 'u-vw-root-mender' ? 0xa8ec79 : 0xe9c65d, 0.9);

  const torso = new THREE.Mesh(
    stalker ? new THREE.CapsuleGeometry(0.24, 0.62, 3, 7) : new THREE.CylinderGeometry(heavy ? 0.4 : 0.25, heavy ? 0.48 : 0.34, heavy ? 0.92 : 0.78, 7),
    bark,
  );
  torso.name = 'torso';
  torso.position.y = stalker ? 0.92 : heavy ? 1.28 : 1.16;
  torso.rotation.x = stalker ? 0.5 : 0;
  group.add(torso);
  for (let i = 0; i < (heavy ? 7 : 4); i++) {
    const plate = petal(leaf, heavy ? 0.34 : 0.24, heavy ? 0.14 : 0.1);
    plate.position.set((i % 2 ? 1 : -1) * (0.18 + (i % 3) * 0.05), torso.position.y + 0.15 - i * 0.1, 0.24);
    plate.rotation.z = (i % 2 ? 1 : -1) * 0.55;
    group.add(plate);
  }
  const core = new THREE.Mesh(new THREE.IcosahedronGeometry(heavy ? 0.14 : 0.1, 0), sap);
  core.position.set(0, torso.position.y, 0.31);
  group.add(core);

  const head = new THREE.Group();
  head.name = 'head';
  head.position.set(0, stalker ? 1.28 : heavy ? 1.95 : 1.7, stalker ? 0.28 : 0);
  group.add(head);
  const face = new THREE.Mesh(new THREE.IcosahedronGeometry(heavy ? 0.25 : 0.2, 1), bark);
  face.scale.set(0.9, 1.05, 0.82);
  head.add(face);
  for (const sx of [-1, 1]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.035, 5, 4), sap);
    eye.position.set(sx * 0.075, 0.02, 0.17);
    head.add(eye);
    const horn = new THREE.Mesh(new THREE.ConeGeometry(0.045, heavy ? 0.42 : 0.3, 5), thorn);
    horn.position.set(sx * 0.15, 0.18, -0.02);
    horn.rotation.z = sx * -0.48;
    head.add(horn);
  }
  const crown = new THREE.Group();
  crown.name = 'headGrowth';
  head.add(crown);
  for (let i = 0; i < 5; i++) {
    const p = petal(leaf, 0.25 + (i % 2) * 0.08, 0.085);
    p.position.set((i - 2) * 0.09, 0.25 + Math.abs(i - 2) * -0.025, -0.03);
    p.rotation.z = (i - 2) * -0.3;
    crown.add(p);
  }

  const legL = limb('legL', barkDark, -0.18, stalker ? 0.68 : 0.78, stalker ? 0.58 : 0.72, heavy ? 0.15 : 0.1);
  const legR = limb('legR', barkDark, 0.18, stalker ? 0.68 : 0.78, stalker ? 0.58 : 0.72, heavy ? 0.15 : 0.1);
  const armL = limb('armL', bark, heavy ? -0.54 : -0.4, heavy ? 1.48 : 1.4, heavy ? 0.82 : 0.7, heavy ? 0.15 : 0.09);
  const armR = limb('armR', bark, heavy ? 0.54 : 0.4, heavy ? 1.48 : 1.4, heavy ? 0.82 : 0.7, heavy ? 0.15 : 0.09);
  group.add(legL, legR, armL, armR);

  const weapon = new THREE.Group();
  weapon.name = 'weapon';
  weapon.position.set(0, -0.58, 0.12);
  if (defId === 'u-vw-vine-archers') {
    const arc = new THREE.Mesh(new THREE.TorusGeometry(0.48, 0.035, 5, 14, Math.PI * 1.3), barkDark);
    arc.rotation.y = Math.PI / 2;
    weapon.add(arc);
    const vine = new THREE.Mesh(new THREE.CylinderGeometry(0.009, 0.009, 0.96, 4), leaf);
    weapon.add(vine);
    weapon.rotation.x = 1.05;
  } else if (defId === 'u-vw-root-mender') {
    const staff = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.055, 1.5, 6), barkDark);
    staff.position.y = 0.5;
    weapon.add(staff);
    const bloom = new THREE.Mesh(new THREE.IcosahedronGeometry(0.22, 1), sap);
    bloom.position.y = 1.32;
    weapon.add(bloom);
    weapon.rotation.x = 0.2;
  } else {
    const club = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.12, heavy ? 1.2 : 0.88, 6), barkDark);
    club.position.y = 0.28;
    weapon.add(club);
    for (let i = 0; i < 4; i++) {
      const spike = new THREE.Mesh(new THREE.ConeGeometry(0.045, 0.25, 5), thorn);
      spike.position.set((i % 2 ? 1 : -1) * 0.1, 0.2 + i * 0.18, 0);
      spike.rotation.z = (i % 2 ? 1 : -1) * 1.0;
      weapon.add(spike);
    }
    weapon.rotation.x = 0.45;
  }
  armL.add(weapon);
  if (defId === 'u-vw-thorn-guard' || heavy) {
    const shield = new THREE.Mesh(new THREE.CylinderGeometry(heavy ? 0.52 : 0.42, heavy ? 0.62 : 0.5, 0.16, 7), bark);
    shield.position.set(heavy ? 0.63 : 0.5, 1.05, 0.32);
    shield.rotation.z = Math.PI / 2;
    group.add(shield);
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const spike = new THREE.Mesh(new THREE.ConeGeometry(0.045, 0.3, 5), thorn);
      spike.position.set((heavy ? 0.63 : 0.5) + Math.cos(a) * 0.38, 1.05 + Math.sin(a) * 0.38, 0.43);
      spike.rotation.z = -a + Math.PI / 2;
      group.add(spike);
    }
  }
  finish(group, uiScale, heavy ? 0.68 : 0.52);
  return {
    group,
    pose: ({ time, phase, moveAmt, strike, knockdown = 0 }) => {
      const stride = Math.sin(phase) * (stalker ? 0.78 : heavy ? 0.46 : 0.62) * moveAmt;
      legL.rotation.x = stride;
      legR.rotation.x = -stride;
      armL.rotation.x = -stride * 0.55 - strike * 0.9;
      armR.rotation.x = stride * 0.55 - strike * 0.42;
      weapon.rotation.x = (defId === 'u-vw-vine-archers' ? 1.05 : defId === 'u-vw-root-mender' ? 0.2 : 0.45) - strike * 0.85;
      torso.rotation.z = Math.sin(phase) * 0.035 * moveAmt;
      torso.scale.y = 1 + Math.sin(time * 1.6 + variant) * 0.025 * (1 - moveAmt);
      head.rotation.y = Math.sin(time * 0.55 + variant) * 0.13 * (1 - moveAmt);
      crown.rotation.z = Math.sin(time * 1.2 + variant) * 0.06 + stride * 0.05;
      const coreMat = core.material as THREE.MeshStandardMaterial;
      coreMat.emissiveIntensity = 0.85 + Math.sin(time * 2.1 + variant) * 0.2 + strike * 0.7;
      group.rotation.z = knockdown * (variant % 2 === 0 ? 1 : -1) * Math.PI * 0.48;
    },
  };
}

function makeVerdantGiant(defId: string, owner: 0 | 1, variant: number, uiScale: number): VerdantSquadRig {
  const group = new THREE.Group();
  const hydra = defId === 'u-vw-thornmaw-hydra';
  const groveback = defId === 'u-vw-groveback';
  const colossal = defId === 'u-vw-verdant-behemoth';
  const bark = mat(mix(colossal ? 0x443923 : 0x5a482d, OWNER_COLORS[owner], 0.08), 1);
  const barkLight = mat(0x7a623c, 0.98);
  const leaf = mat(0x547f3b, 1);
  const thorn = mat(0xa27a43, 0.92);
  const life = glow(0x86d45f, 1.15);
  const torso = new THREE.Mesh(
    groveback ? new THREE.SphereGeometry(0.78, 9, 7) : new THREE.CylinderGeometry(colossal ? 0.72 : 0.52, colossal ? 0.92 : 0.68, colossal ? 1.65 : 1.25, 8),
    bark,
  );
  torso.name = 'torso';
  torso.position.y = groveback ? 0.92 : colossal ? 1.38 : 1.15;
  if (groveback) torso.scale.set(1.15, 0.7, 1.45);
  group.add(torso);
  const core = new THREE.Mesh(new THREE.IcosahedronGeometry(colossal ? 0.24 : 0.17, 0), life);
  core.position.set(0, torso.position.y, groveback ? 0.74 : colossal ? 0.7 : 0.52);
  group.add(core);

  const legs: THREE.Group[] = [];
  const legPositions = groveback
    ? [[-0.52, 0.52], [0.52, 0.52], [-0.52, -0.52], [0.52, -0.52]]
    : [[-0.36, 0], [0.36, 0]];
  legPositions.forEach(([x, z], i) => {
    const leg = limb(`leg${i}`, barkLight, x, groveback ? 0.68 : 0.88, groveback ? 0.72 : colossal ? 1.0 : 0.82, colossal ? 0.22 : 0.17);
    leg.position.z = z;
    group.add(leg);
    legs.push(leg);
  });
  const arms: THREE.Group[] = [];
  if (!groveback) {
    for (const sx of [-1, 1]) {
      const arm = limb(sx < 0 ? 'armL' : 'armR', bark, sx * (colossal ? 0.9 : 0.68), colossal ? 1.65 : 1.42, colossal ? 1.18 : 0.92, colossal ? 0.23 : 0.18);
      const fist = new THREE.Mesh(new THREE.DodecahedronGeometry(colossal ? 0.32 : 0.24, 0), barkLight);
      fist.position.y = -(colossal ? 1.15 : 0.92);
      arm.add(fist);
      group.add(arm);
      arms.push(arm);
    }
  }
  const heads: THREE.Group[] = [];
  const headCount = hydra ? 3 : 1;
  for (let i = 0; i < headCount; i++) {
    const head = new THREE.Group();
    head.name = `head${i}`;
    const a = headCount === 1 ? 0 : (i - 1) * 0.62;
    head.position.set(Math.sin(a) * 0.56, colossal ? 2.45 : groveback ? 1.28 : 1.95, groveback ? 0.55 : Math.cos(a) * 0.3);
    const skull = new THREE.Mesh(new THREE.IcosahedronGeometry(colossal ? 0.34 : 0.25, 1), bark);
    skull.scale.set(0.9, 0.8, 1.25);
    head.add(skull);
    const mouth = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.1, 0.38), thorn);
    mouth.position.set(0, -0.1, 0.25);
    head.add(mouth);
    for (const sx of [-1, 1]) {
      const horn = new THREE.Mesh(new THREE.ConeGeometry(0.07, 0.5, 5), thorn);
      horn.position.set(sx * 0.18, 0.18, -0.03);
      horn.rotation.z = sx * -0.55;
      head.add(horn);
    }
    group.add(head);
    heads.push(head);
  }
  for (let i = 0; i < (colossal ? 14 : 9); i++) {
    const a = (i / (colossal ? 14 : 9)) * Math.PI * 2;
    const growth = i % 3 === 0
      ? new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.55, 5), thorn)
      : petal(leaf, 0.36, 0.13);
    growth.position.set(Math.cos(a) * (groveback ? 0.72 : 0.52), torso.position.y + 0.45 + Math.sin(a * 2) * 0.25, Math.sin(a) * (groveback ? 0.82 : 0.42));
    growth.rotation.z = -a;
    group.add(growth);
  }
  finish(group, uiScale, colossal ? 1.05 : 0.82);
  return {
    group,
    pose: ({ time, phase, moveAmt, strike }) => {
      legs.forEach((leg, i) => { leg.rotation.x = Math.sin(phase + i * Math.PI) * 0.42 * moveAmt; });
      arms.forEach((arm, i) => { arm.rotation.x = Math.sin(phase + i * Math.PI) * 0.25 * moveAmt - strike * (i ? 0.65 : 1.1); });
      heads.forEach((head, i) => {
        head.rotation.y = Math.sin(time * 0.7 + i * 1.5 + variant) * (0.12 + (hydra ? 0.13 : 0));
        head.rotation.x = -strike * (0.18 + i * 0.05);
      });
      torso.rotation.z = Math.sin(phase) * 0.025 * moveAmt;
      torso.scale.y = (groveback ? 0.7 : 1) * (1 + Math.sin(time * 1.25 + variant) * 0.025);
      const coreMat = core.material as THREE.MeshStandardMaterial;
      coreMat.emissiveIntensity = 1.05 + Math.sin(time * 2 + variant) * 0.22 + strike * 0.8;
    },
  };
}

export function makeVerdantSquadRig(
  defId: string,
  owner: 0 | 1,
  variant: number,
  uiScale: number,
): VerdantSquadRig {
  switch (defId) {
    case 'u-vw-sproutling-swarm': return makeSproutling(owner, variant % 8, uiScale);
    case 'u-vw-sporecallers': return makeSporecaller(owner, variant % 3, uiScale);
    case 'u-vw-sunpetal-seraphs': return makeSeraph(owner, variant % 3, uiScale);
    case 'u-vw-groveback':
    case 'u-vw-ancient-treants':
    case 'u-vw-thornmaw-hydra':
    case 'u-vw-verdant-behemoth': return makeVerdantGiant(defId, owner, variant, uiScale);
    default: return makeVerdantGuardian(defId, owner, variant, uiScale);
  }
}
